import type { Identifier } from "ra-core";
import { useGetList } from "ra-core";
import { useMemo } from "react";

import type { AdvisorGoal, Deal, IncentiveProgram, Task } from "../types";
import { CLOSED_WON_STAGE, DIRECT_SALE_LEAD_TYPES } from "./directSales";
import { getCurrentMonthPeriod, periodMonthKey } from "./periods";

const MEETING_TASK_TYPE = "meeting";

/**
 * DOMAIN RULE (c) (ASSUMPTION, no appointment entity yet): a proactive
 * appointment's contact must belong to one of the advisor's `proactivo`
 * leads, regardless of that lead's stage -- so this lead type is queried on
 * its own, never scoped to `CLOSED_WON_STAGE`.
 */
const PROACTIVE_LEAD_TYPE = "proactivo";

/**
 * Generous, fixed per-query cap: keeps every query bounded and independent
 * of how many advisors are requested (one request each, never one request
 * per advisor), rather than an exact per-advisor count -- reasonable for a
 * period-scoped read (`.claude/rules/coding-style.md`: KISS over a
 * GROUP-BY-shaped API this data provider doesn't expose).
 */
const MAX_DEALS_PER_QUERY = 1000;
const MAX_TASKS_PER_QUERY = 2000;

export type AdvisorProgressData = {
  /** The incentive-program catalogue (DATA CONTRACT E7: 5 seeded rows today). */
  programs: IncentiveProgram[];
  /** This period's goal rows for the requested advisors (any scope). */
  goals: AdvisorGoal[];
  /**
   * Every direct-sale-eligible deal owned by one of `advisorIds` -- the
   * union of this period's closed-won direct sales and any `proactivo` lead
   * (any stage, needed for the appointment-eligibility check), deduplicated
   * by id. Callers slice it to a period via `directSales.ts`/
   * `incentiveProgress.ts`.
   */
  deals: Deal[];
  /** Every `meeting`-type task with a `done_date` owned by one of `advisorIds`. */
  tasks: Task[];
  isPending: boolean;
  error: unknown;
  /**
   * True when any of the bounded queries above hit its cap (its `total`
   * exceeds the rows actually returned) -- consumers must not present the
   * resulting numbers as complete when this is true.
   */
  isTruncated: boolean;
};

/**
 * Loads the raw inputs `incentiveProgress.ts` needs for one or several
 * advisors, for the calendar month containing `now` (goals are monthly --
 * DOMAIN RULE (g)). A fixed number of queries regardless of how many
 * advisors are requested: the program catalogue, the month's goal rows,
 * this period's closed-won direct sales, the `proactivo` leads (kept
 * separate since they matter regardless of stage), and the done meeting
 * tasks -- each batched via `sales_id@in` and capped at a generous
 * per-query limit.
 */
export function useAdvisorProgress(
  advisorIds: Identifier[],
  now: Date = new Date(),
): AdvisorProgressData {
  const monthKey = periodMonthKey(getCurrentMonthPeriod(now));
  const hasAdvisors = advisorIds.length > 0;
  const idList = useMemo(() => `(${advisorIds.join(",")})`, [advisorIds]);

  const programsResult = useGetList<IncentiveProgram>("incentive_programs", {
    pagination: { page: 1, perPage: 50 },
  });

  const goalsResult = useGetList<AdvisorGoal>(
    "advisor_goals",
    {
      pagination: { page: 1, perPage: Math.max(advisorIds.length * 2, 1) },
      filter: { "sales_id@in": idList, period_month: monthKey },
    },
    { enabled: hasAdvisors },
  );

  // DOMAIN RULE (a): closed-won direct sales only -- filtering by stage here
  // (instead of loading every stage) keeps this query's size proportional to
  // actual sales, not to the team's whole lead volume.
  const directSalesResult = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: MAX_DEALS_PER_QUERY },
      sort: { field: "updated_at", order: "DESC" },
      filter: {
        "sales_id@in": idList,
        stage: CLOSED_WON_STAGE,
        "lead_type@in": `(${DIRECT_SALE_LEAD_TYPES.join(",")})`,
        "is_test_data@neq": true,
      },
    },
    { enabled: hasAdvisors },
  );

  // DOMAIN RULE (c): the proactive-appointment check needs a proactivo
  // lead's contact_ids regardless of stage, so it cannot reuse the
  // closed-won-only query above.
  const proactiveLeadsResult = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: MAX_DEALS_PER_QUERY },
      sort: { field: "updated_at", order: "DESC" },
      filter: {
        "sales_id@in": idList,
        lead_type: PROACTIVE_LEAD_TYPE,
        "is_test_data@neq": true,
      },
    },
    { enabled: hasAdvisors },
  );

  const tasksResult = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: MAX_TASKS_PER_QUERY },
      sort: { field: "done_date", order: "DESC" },
      filter: {
        "sales_id@in": idList,
        type: MEETING_TASK_TYPE,
        "done_date@not.is": null,
      },
    },
    { enabled: hasAdvisors },
  );

  const deals = useMemo(() => {
    const byId = new Map<Identifier, Deal>();
    for (const deal of directSalesResult.data ?? []) byId.set(deal.id, deal);
    for (const deal of proactiveLeadsResult.data ?? []) byId.set(deal.id, deal);
    return Array.from(byId.values());
  }, [directSalesResult.data, proactiveLeadsResult.data]);

  const isQueryTruncated = (result: {
    data?: unknown[];
    total?: number;
  }): boolean => (result.total ?? 0) > (result.data?.length ?? 0);

  return {
    programs: programsResult.data ?? [],
    goals: goalsResult.data ?? [],
    deals,
    tasks: tasksResult.data ?? [],
    // `enabled: hasAdvisors` queries never fetch when `advisorIds` is empty
    // (a lider with an empty team), so react-query keeps their `isPending`
    // permanently true -- their pending state is only meaningful once there
    // is actually something to load (bug fix, TASK-090/TASK-089).
    isPending:
      programsResult.isPending ||
      (hasAdvisors &&
        (goalsResult.isPending ||
          directSalesResult.isPending ||
          proactiveLeadsResult.isPending ||
          tasksResult.isPending)),
    error:
      programsResult.error ??
      goalsResult.error ??
      directSalesResult.error ??
      proactiveLeadsResult.error ??
      tasksResult.error,
    isTruncated:
      isQueryTruncated(goalsResult) ||
      isQueryTruncated(directSalesResult) ||
      isQueryTruncated(proactiveLeadsResult) ||
      isQueryTruncated(tasksResult),
  };
}
